"use-client";

import {
  BasicLocationSummary,
  StateAbbreviation,
} from "@nimble/data-service-client/types";
import type { DistrictNcesId } from "@nimble/datamodel/types/ncesId";
import * as React from "react";

import { MultiSelectProps } from "@/components/shared/multi-select/multi-select";
import {
  ListItemProps,
  MultiSelectOption,
} from "@/components/shared/multi-select/multi-select.view";
import RemoteMultiselect from "@/components/shared/multi-select/remote-multi-select";
import useSchoolsAutocomplete from "@/hooks/data/use-schools-autocomplete";

interface SchoolSelectProps<T = string>
  extends Omit<MultiSelectProps<T>, "options" | "title"> {
  title?: string;
  state?: StateAbbreviation | null;
  districts?: DistrictNcesId[];
}

interface SchoolOption extends MultiSelectOption<string> {
  physical_address?: BasicLocationSummary;
}

function SchoolListItem({ option }: ListItemProps<string>) {
  const schoolOption = option as SchoolOption;
  const city = schoolOption.physical_address?.city;
  return (
    <div className="flex flex-wrap gap-1">
      <strong>{option.label}</strong>
      {city && <span>{city}</span>}
    </div>
  );
}

function getUseSchoolsAutocompleteWithScope(
  states: StateAbbreviation[],
  districts: DistrictNcesId[],
) {
  return (schools?: string[] | null, search?: string | null) => {
    return useSchoolsAutocomplete(
      schools ?? null,
      search ?? null,
      states,
      districts,
    );
  };
}

export default function SchoolSelect({
  value,
  onChange,
  title,
  state,
  districts,
  ...props
}: SchoolSelectProps) {
  return (
    <RemoteMultiselect
      value={value}
      onChange={onChange}
      title={title || "Schools"}
      useAutocomplete={getUseSchoolsAutocompleteWithScope(
        state ? [state] : [],
        districts ?? [],
      )}
      ListItem={SchoolListItem}
      {...props}
    />
  );
}
